import React from 'react';

/*
 * A mark for each hat.
 *
 * These ride on the planets in the orrery, where they are drawn at roughly
 * 20px across on a textured, moving, arbitrarily-coloured surface. That budget
 * is the whole design constraint: three or four strokes each, no interior
 * detail, and silhouettes that stay distinct from one another when they are
 * this small. Anything finer turns to mush at the far side of an orbit.
 *
 * Each glyph is authored in a 24x24 box centred on the origin, so callers can
 * scale by (size / 24) and place it at the middle of whatever it labels.
 *
 * Shapes come in two kinds. Closed ones are filled; open ones carry `line: true`
 * and are stroked instead. Both take their colour from `--ink`, which is what
 * lets the same geometry be drawn twice -- once fat and dark as a halo, once in
 * ink on top -- without duplicating any path data.
 */

export const HAT_GLYPH = {
  /*
   * Architect: a set square. The compass this replaced read as a clown hat --
   * a dot above a splayed triangle is a hat before it is an instrument. A right
   * angle has no such second reading, and no other mark here is an outline
   * triangle, so the silhouette stays unique.
   */
  arch: [{ d: 'M -8.5 -8 L -8.5 8 L 9.5 8 Z', line: true }],
  // DevOps: the loop. The one glyph in the set that is already a convention.
  devops: [
    {
      d: 'M 0 0 C -2.2 -4.8 -9.8 -4.8 -9.8 0 C -9.8 4.8 -2.2 4.8 0 0 C 2.2 -4.8 9.8 -4.8 9.8 0 C 9.8 4.8 2.2 4.8 0 0 Z',
      line: true,
    },
  ],
  // SRE: a trace with a beat in it -- the thing you watch to know it is alive.
  sre: [{ d: 'M -10.5 1 L -5.5 1 L -2.5 -8 L 1.5 7 L 4 1 L 10.5 1', line: true }],
  // Security: a shield.
  sec: [
    { d: 'M 0 -10 L 8.5 -6.4 L 8.5 1 C 8.5 6.4 4.6 9.4 0 10.5 C -4.6 9.4 -8.5 6.4 -8.5 1 L -8.5 -6.4 Z' },
  ],
  // Developer: angle brackets. The slash is dropped -- three marks is too many.
  dev: [
    { d: 'M -3.5 -8.5 L -9.5 0 L -3.5 8.5', line: true },
    { d: 'M 3.5 -8.5 L 9.5 0 L 3.5 8.5', line: true },
  ],
  // Tester: the gate passing.
  test: [{ d: 'M -9 0.5 L -2.5 7.5 L 9 -7', line: true }],
  // UX: a pointer, because this hat is about the hand on the other end.
  ux: [{ d: 'M -7 -9 L 7 0 L 0 1 L 4 8 L 1 9 L -3 2 L -7 6 Z' }],
  // Designer: a nib, vent and all.
  design: [
    {
      d: 'M 0 -10 L 7 3 L 0 10 L -7 3 Z M -2.2 3.5 A 2.2 2.2 0 1 0 2.2 3.5 A 2.2 2.2 0 1 0 -2.2 3.5 Z',
      evenodd: true,
    },
  ],
  // AI: a spark, cut with concave sides so it reads as light rather than a plus.
  ai: [{ d: 'M 0 -11 Q 0 0 11 0 Q 0 0 0 11 Q 0 0 -11 0 Q 0 0 0 -11 Z' }],
};

const shapes = (hatId) =>
  (HAT_GLYPH[hatId] || []).map((shape, index) => (
    <path
      key={index}
      d={shape.d}
      className={shape.line ? 'hat-glyph-line' : undefined}
      fillRule={shape.evenodd ? 'evenodd' : undefined}
    />
  ));

/**
 * The glyph as SVG content, for placing inside an existing <svg>.
 *
 * `halo` draws a fattened dark copy underneath first, which is what keeps the
 * mark legible on a planet whose surface colour is not known in advance -- the
 * same trick `paint-order: stroke` does for the text elsewhere on the page.
 */
export const HatGlyph = ({ hatId, size = 24, halo = false }) => {
  const scale = size / 24;
  return (
    <g className="hat-glyph" transform={scale === 1 ? undefined : `scale(${scale.toFixed(3)})`}>
      {halo && <g className="hat-glyph-halo">{shapes(hatId)}</g>}
      <g className="hat-glyph-ink">{shapes(hatId)}</g>
    </g>
  );
};

/** The glyph as a standalone element, for chips and headings in normal flow. */
const HatIcon = ({ hatId, size = 16, className = '' }) => (
  <svg
    className={`hat-icon ${className}`.trim()}
    viewBox="-12 -12 24 24"
    width={size}
    height={size}
    aria-hidden="true"
    focusable="false"
  >
    <g className="hat-glyph">
      <g className="hat-glyph-ink">{shapes(hatId)}</g>
    </g>
  </svg>
);

export default HatIcon;
